import { getInfCLogger } from '@infodata-group/inf-commons-core';
import { inject, Injectable } from '@angular/core';
import { AppConfig } from '../engine/app-config';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Subject, Subscription, timer } from 'rxjs';
import { WebsocketEventsType } from '../types/websocket/websocket-event.type';

const logger = getInfCLogger('WebSocketService');

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  private readonly appConfig = inject(AppConfig);

  public get maxRetry(): number {
    return this.appConfig.websocketConfig.maxRetry || 10;
  }
  public get delayRetry(): number {
    return this.appConfig.websocketConfig.delayRetry || 5000;
  }
  public get retryAfter(): number {
    return this.appConfig.websocketConfig.retryAfter || 300000;
  }

  protected webSocket: WebSocketSubject<WebsocketEventsType | string>;

  private events$$ = new Subject<WebsocketEventsType>();
  public events$ = this.events$$.asObservable();

  private status$$ = new Subject<'connected' | 'disconnected'>();
  public status$ = this.status$$.asObservable();

  private authToken = '';

  private isConnected = false;
  private reconnectAttempts = 0;
  private reconnectSub: Subscription;

  private pingInterval = 30000;
  private lastPingTimestamp: number = null;
  private pingCheckSub: Subscription = null;

  private createWebSocket() {
    this.webSocket = webSocket({
      url: `${this.appConfig.websocketConfig.url}?token=${this.authToken}`,
      deserializer: (e: MessageEvent) => {
        if (typeof e.data === 'string' && e.data === 'ping') {
          this.lastPingTimestamp = Date.now();
          this.webSocket.next('pong');
          return null;
        }
        try {
          return JSON.parse(e.data);
        } catch (e) {
          logger.error('Error parsing WebSocket message', e);
          return null;
        }
      },
    });
  }

  private startPingCheck() {
    logger.debug('Starting WebSocket ping check', this.pingInterval);
    if (this.pingCheckSub) {
      this.pingCheckSub.unsubscribe();
    }
    this.pingCheckSub = timer(this.pingInterval, this.pingInterval).subscribe({
      next: () => {
        logger.debug('Checking WebSocket ping status');
        if (
          this.lastPingTimestamp &&
          Date.now() - this.lastPingTimestamp > 3 * this.pingInterval
        ) {
          logger.error('No ping received in the last 3 intervals, disconnecting');
          // Ne pas supprimer le token pour permettre la reconnexion
          this.disconnect(false);
          this.handleReconnect();
        }
      },
    });
  }

  private stopPingCheck() {
    if (this.pingCheckSub) {
      this.pingCheckSub.unsubscribe();
      this.pingCheckSub = null;
    }
  }

  public connect(authToken: string) {
    this.authToken = authToken;
    if (!this.appConfig.websocketConfig) {
      logger.error(`Trying to connect to websocket without config`);
      return;
    }
    if (!this.appConfig.websocketConfig.url) {
      return;
    }
    if (!this.authToken) {
      logger.error(`Trying to connect to websocket without token`);
      return;
    }

    this.reconnectAttempts = 0;
    this.tryConnect();
  }

  private tryConnect() {
    logger.debug('Connecting to WebSocket');
    this.createWebSocket();
    this.webSocket.subscribe({
      next: (msg) => {
        if (!this.isConnected) {
          logger.debug('WebSocket connected');
          this.status$$.next('connected');
          this.lastPingTimestamp = Date.now();
          this.startPingCheck();
        }
        this.isConnected = true;
        this.reconnectAttempts = 0; // reset on success
        if (!msg) {
          return;
        }
        if (typeof msg === 'string') {
          return;
        }
        // If config, update the websocketConfig
        if (msg.type === 'websocket-config') {
          this.appConfig.websocketConfig = {
            ...this.appConfig.websocketConfig,
            ...msg.payload,
          };
          if (msg.payload && msg.payload.pingInterval) {
            this.pingInterval = msg.payload.pingInterval;
            this.startPingCheck();
          }
          logger.debug('WebSocket configuration updated', msg.payload);
          return;
        }
        this.events$$.next(msg);
      },
      error: (err) => {
        this.status$$.next('disconnected');
        this.isConnected = false;
        logger.debug('Websocket closed due to error', err);
        this.stopPingCheck();
        this.handleReconnect();
      },
      complete: () => {
        this.status$$.next('disconnected');
        logger.debug('WebSocket connection closed');
        this.webSocket = null;
        this.stopPingCheck();
        this.handleReconnect();
      },
    });
  }

  private handleReconnect() {
    this.reconnectAttempts++;
    let delay = this.delayRetry;
    if (this.reconnectAttempts > this.maxRetry) {
      delay = this.retryAfter;
      this.reconnectAttempts = 0; // reset after waiting retryAfter
    }
    if (this.reconnectSub) {
      this.reconnectSub.unsubscribe();
    }
    this.reconnectSub = timer(delay).subscribe(() => {
      /* istanbul ignore else */
      if (this.authToken) {
        this.tryConnect();
      }
    });
  }

  public disconnect(clearToken = true) {
    if (this.webSocket) {
      this.webSocket.complete();
      this.webSocket = null;
      if (clearToken) {
        this.authToken = '';
      }
    }
    if (this.reconnectSub) {
      this.reconnectSub.unsubscribe();
      this.reconnectSub = null;
    }
    this.stopPingCheck();
    this.lastPingTimestamp = null;
  }

  public send(message: WebsocketEventsType) {
    this.webSocket.next(message);
  }
}
